'use client'

import { Palette } from 'lucide-react'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { LandingDesign } from '@/features/landing-page/landing-design'
import { useLandingDesign } from '@/features/landing-page/use-landing-design'

/**
 * Landing design-preview menu, always visible to eligible accounts (superusers + whitelisted email
 * domains, see LANDING_PREVIEW_EMAIL_DOMAINS). Lets them preview the OLD or NEW landing design (or
 * reset to the global default) — the choice is stored per-user in localStorage and never affects the
 * public. Renders nothing for everyone else / logged-out visitors.
 */
export function LandingSuperUserMenu() {
    const { effective, constant, override, canPreview, setOverride } = useLandingDesign()

    if (!canPreview) return null

    const options: { label: string; value: LandingDesign | null }[] = [
        { label: `Default (${constant})`, value: null },
        { label: 'Old design', value: 'old' },
        { label: 'New design', value: 'new' },
    ]

    return (
        <DropdownMenu>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    // Stacked above (and z-indexed over) the temp WAF captcha test button, which is also
                    // fixed bottom-4 left-4 (z-100) on dev/staging — otherwise it covers this menu.
                    className="fixed bottom-16 left-4 z-[110] flex items-center gap-1.5 rounded-full bg-[#171717] px-3 py-2 text-[12px] font-medium text-white/80 shadow-lg transition-colors hover:text-white"
                >
                    <Palette className="size-3.5" />
                    Landing: {effective}
                </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" side="top" className="w-56">
                <DropdownMenuLabel>Landing design preview</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {options.map((option) => {
                    const isActive = option.value === override
                    return (
                        <DropdownMenuItem key={option.label} onClick={() => setOverride(option.value)}>
                            {option.label}
                            {isActive && <span className="ml-auto text-[11px] text-[#FF631B]">on</span>}
                        </DropdownMenuItem>
                    )
                })}
            </DropdownMenuContent>
        </DropdownMenu>
    )
}
